import { useNavigate } from 'react-router-dom';
import { 
 
  Camera, 
  Wand2, 
  Palette, 
  Volume2, 
  FileDown, 
  ShieldCheck,
  Star,
  BookOpen,
  PenLine
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useCreationStore } from '../store/useCreationStore';

// UI-UX-Pro-Max: 使用 SVG 图标代替 emoji
const features = [
  { 
    icon: Camera, 
    title: '上传照片', 
    desc: '上传孩子的照片，AI将生成专属角色',
  },
  { 
    icon: Wand2, 
    title: '智能创作', 
    desc: 'AI根据主题自动生成精彩故事',
  },
  { 
    icon: Palette, 
    title: '精美插图', 
    desc: '每页配有独特的AI生成插图',
  },
  { 
    icon: Volume2, 
    title: '语音朗读', 
    desc: '自动合成语音，给孩子讲故事',
  },
  { 
    icon: FileDown, 
    title: 'PDF下载', 
    desc: '生成精美PDF，随时阅读分享',
  },
  { 
    icon: ShieldCheck, 
    title: '安全审核', 
    desc: '多重内容检查，确保内容健康',
  },
];

// 用户评价
const testimonials = [
  { name: '小明妈妈', rating: 5, comment: '孩子超喜欢！每晚都要听AI生成的故事。' },
  { name: '乐乐爸爸', rating: 5, comment: '操作简单，生成的绘本质量很高。' },
  { name: '朵朵妈妈', rating: 5, comment: '终于不用为讲故事发愁了，推荐！' },
];

function Home() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // 检查登录状态，未登录跳转到登录页
  const handleCreateClick = (flow: 'ai' | 'diy') => {
    // 清空之前的创建状态，确保开始全新项目
    useCreationStore.getState().reset();
    useCreationStore.getState().setFlow(flow);
    if (isAuthenticated) {
      navigate(`/create?flow=${flow}`);
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="w-full min-h-screen">
      <div className="w-full flex justify-center">
        <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-2 md:pt-3 pb-3 md:pb-6 lg:pb-8 text-center">
        {/* ========================================
            Hero Section - Exaggerated Minimalism
            ======================================== */}
        <section className="text-center py-5 md:py-8 lg:py-10 flex flex-col items-center space-y-4 md:space-y-5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center space-y-4 md:space-y-5"
          >
            {/* Logo Icon */}
            <div className="mb-1 md:mb-0">
              <motion.div 
                className="inline-flex items-center justify-center w-14 h-14 md:w-20 md:h-20 rounded-2xl bg-indigo-600 text-white shadow-md shadow-indigo-500/10"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <BookOpen className="w-8 h-8 md:w-12 md:h-12" />
              </motion.div>
            </div>
            
            {/* Main Heading */}
            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
              <span className="gradient-text">
                AI绘本生成器
              </span>
            </h1>
            
            {/* Subtitle - 居中对齐 */}
            <p className="font-body text-base md:text-xl lg:text-2xl text-indigo-900/70 max-w-2xl leading-relaxed text-center px-2 md:px-0">
              为您的孩子创造独一无二的绘本故事
            </p>
            
            {/* Description */}
            <p className="text-sm md:text-base lg:text-lg text-indigo-900/50 leading-relaxed">
              上传照片 → AI生成角色 → 创作故事 → 精美插图 → PDF下载
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-5 justify-center px-4 sm:px-0 pt-1 md:pt-0 w-full max-w-xs sm:max-w-none">
              <button
                onClick={() => handleCreateClick('diy')}
                className="btn-primary rounded-xl h-12 md:h-14 px-6 md:px-8 text-base md:text-lg font-semibold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                <PenLine className="w-5 h-5" />
                亲子自制绘本
              </button>
            </div>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div 
            className="flex flex-wrap justify-center gap-4 md:gap-8 text-xs md:text-sm text-slate-500 pt-1 md:pt-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <span className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 md:w-4 md:h-4 text-indigo-600 fill-indigo-600" />
              4.9分用户好评
            </span>
            <span className="hidden sm:inline">•</span>
            <span>已生成 10,000+ 本绘本</span>
            <span className="hidden sm:inline">•</span>
            <span>新用户送 ¥10 体验金</span>
          </motion.div>
        </section>

        {/* ========================================
            Features Section
            ======================================== */}
        <section className="pt-3 pb-5 md:pb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* 标题 */}
            <h2 className="font-display text-2xl md:text-4xl font-bold text-center mb-3 md:mb-6 text-indigo-900 leading-snug">
              功能特色
            </h2>
            {/* 副标题 */}
            <p className="text-center text-indigo-900/60 mb-8 md:mb-12 text-sm md:text-lg leading-relaxed">
              简单几步，为孩子创作专属绘本
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-5 md:p-6 shadow-sm border border-slate-200 hover:-translate-y-1 hover:shadow-md transition-all cursor-pointer text-center flex flex-col items-center"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-indigo-100 flex items-center justify-center mb-3 md:mb-4 text-indigo-600">
                  <feature.icon className="w-6 h-6 md:w-7 md:h-7" />
                </div>
                
                <h3 className="font-display text-base md:text-xl font-bold text-indigo-900 mb-2 md:mb-3 leading-snug">
                  {feature.title}
                </h3>
                <p className="text-sm md:text-[15px] text-indigo-900/60 leading-relaxed">
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* 间隙分隔 */}
        <div className="h-2" />

        {/* ========================================
            How It Works Section - 修复重叠问题
            ======================================== */}
        <section>
          <div className="bg-indigo-600 rounded-2xl text-white relative overflow-hidden p-5 md:p-8">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl" />
            </div>
            
            <div className="relative z-10">
              <h2 className="font-display text-2xl md:text-4xl font-bold text-center mb-6 md:mb-10 leading-snug">
                三步开启AI绘本之旅
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6 md:gap-10 items-start">
                {[
                  { step: '01', title: '上传照片', desc: '上传孩子的照片，AI将学习面部特征' },
                  { step: '02', title: '选择主题', desc: '选择孩子喜欢的故事主题和风格' },
                  { step: '03', title: '生成绘本', desc: 'AI创作故事、生成插图、合成语音' },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 }}
                    className="text-center flex flex-col items-center"
                  >
                    <div className="w-14 h-14 md:w-20 md:h-20 mb-3 md:mb-5 rounded-full bg-white/10 flex items-center justify-center border border-white/20">
                      <span className="font-display text-xl md:text-3xl font-bold">{item.step}</span>
                    </div>
                    <h3 className="font-display text-base md:text-xl font-bold mb-1 md:mb-2 leading-snug">{item.title}</h3>
                    <p className="text-sm md:text-base text-white/80 leading-relaxed max-w-xs">{item.desc}</p>

                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 间隙分隔 - 24px */}
        <div className="h-6" />

        {/* ========================================
            Testimonials Section - 增加板块间距
            ======================================== */}
        <section>
          {/* 标题 */}
          <h2 className="font-display text-2xl md:text-4xl font-bold text-center mb-6 md:mb-10 text-indigo-900 leading-snug">
            用户评价
          </h2>
          
          {/* 评价卡片间距 */}
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {testimonials.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-5 md:p-6 shadow-sm border border-slate-200 text-center flex flex-col items-center"
              >
                <div className="flex gap-1 mb-3 md:mb-4 justify-center">
                  {[...Array(item.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 md:w-5 md:h-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-sm md:text-[15px] text-indigo-900/80 mb-4 md:mb-5 leading-relaxed">&ldquo;{item.comment}&rdquo;</p>
                <p className="text-sm md:text-[15px] font-bold text-indigo-900">— {item.name}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* 间隙分隔 - 24px */}
        <div className="h-6" />

        {/* ========================================
            CTA Section - 增加板块间距和内边距
            ======================================== */}
        <section className="pb-10 md:pb-16">
          <motion.div 
            className="bg-white rounded-2xl p-6 md:p-12 shadow-sm border border-slate-200 text-center relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-100 rounded-full blur-3xl opacity-50" />
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-100 rounded-full blur-3xl opacity-50" />
            
            <div className="relative z-10 w-full flex flex-col items-center text-center">
              <h2 className="font-display text-2xl md:text-4xl font-bold mb-3 md:mb-6 text-indigo-900 leading-snug">
                准备好开始了吗？
              </h2>
              <p className="text-indigo-900/60 max-w-lg text-sm md:text-lg leading-relaxed text-center mb-5 md:mb-8">
                只需几分钟，即可为孩子创造一本独特的绘本
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center w-full">
                <button
                  onClick={() => handleCreateClick('diy')}
                  className="btn-cta rounded-xl h-12 md:h-14 px-6 md:px-8 text-base md:text-lg font-semibold bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
                >
                  <PenLine className="w-5 h-5" />
                  亲子自制绘本
                </button>
              </div>
              <p className="mt-4 md:mt-6 text-xs md:text-sm text-indigo-900/40">
                新用户注册即送 ¥10 体验金
              </p>
            </div>
          </motion.div>
        </section>

        {/* 间隙分隔 - 24px */}
        <div className="h-6" />
      </div>
    </div>
  </div>
  );
}

export default Home;
